/**
 * Client Matrix Chart Component
 *
 * Multi-series chart for client matrix metrics (Receita, Custos, Margem).
 * Supports toggling visibility of each series via selectedMetrics prop.
 * Supports comparison data with side-by-side bars.
 *
 * Dark theme styled for Monutrack design system.
 */

import { useMemo } from 'react';

// Metric definition with color and label
export interface ClientMetricConfig {
  key: string;
  label: string;
  color: string;
  type: 'bar' | 'line';
}

// All available Client Matrix metrics (dark theme colors)
export const CLIENT_METRICS: ClientMetricConfig[] = [
  { key: 'receita', label: 'Receita', color: '#3b82f6', type: 'bar' }, // Blue
  { key: 'custos', label: 'Custos', color: '#ef4444', type: 'bar' }, // Monutrack red
  { key: 'margem', label: 'Margem', color: '#D2FA52', type: 'line' }, // Monumenta green
];

// Chart data point with all metrics
export interface ClientChartDataPoint {
  period: string;
  label: string;
  receita: number;
  custos: number;
  margem: number;
}

export type ClientChartType = 'bar' | 'line' | 'combo';

interface ClientMatrixChartProps {
  data: ClientChartDataPoint[];
  comparisonData?: ClientChartDataPoint[];
  selectedMetrics: Set<string>;
  chartType?: ClientChartType;
  height?: number;
}

// Format currency in compact form
function formatCompactCurrency(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(0)}K`;
  }
  return `R$ ${value.toFixed(0)}`;
}

// Lighten a hex color
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
  const B = Math.min(255, (num & 0x0000ff) + amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

export function ClientMatrixChart({
  data,
  comparisonData,
  selectedMetrics,
  chartType = 'combo',
  height = 280,
}: ClientMatrixChartProps): JSX.Element {
  const showComparison = !!comparisonData && comparisonData.length > 0;

  // Filter to only selected metrics
  const activeMetrics = useMemo(() => {
    return CLIENT_METRICS.filter(m => selectedMetrics.has(m.key));
  }, [selectedMetrics]);

  // Determine which metrics render as bars vs lines based on chartType
  const { barMetrics, lineMetrics } = useMemo(() => {
    if (chartType === 'bar') {
      return { barMetrics: activeMetrics, lineMetrics: [] as ClientMetricConfig[] };
    } else if (chartType === 'line') {
      return { barMetrics: [] as ClientMetricConfig[], lineMetrics: activeMetrics };
    } else {
      return {
        barMetrics: activeMetrics.filter(m => m.type === 'bar'),
        lineMetrics: activeMetrics.filter(m => m.type === 'line'),
      };
    }
  }, [activeMetrics, chartType]);

  const chartConfig = useMemo(() => {
    if (data.length === 0 || activeMetrics.length === 0) {
      return null;
    }

    // Calculate max value for scaling across all selected metrics (including comparison)
    const allValues = data.flatMap(d =>
      activeMetrics.map(m => Math.abs(d[m.key as keyof ClientChartDataPoint] as number))
    );
    if (comparisonData) {
      comparisonData.forEach(d => {
        activeMetrics.forEach(m => {
          allValues.push(Math.abs(d[m.key as keyof ClientChartDataPoint] as number));
        });
      });
    }
    const maxValue = Math.max(...allValues, 1);

    return { maxValue };
  }, [data, comparisonData, activeMetrics]);

  if (!chartConfig || data.length === 0 || activeMetrics.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#a0a0a0',
        background: '#141414',
        borderRadius: '12px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        Selecione métricas para exibir no gráfico
      </div>
    );
  }

  const { maxValue } = chartConfig;

  // Chart dimensions
  const paddingLeft = 70;
  const paddingRight = 150;
  const paddingTop = 20;
  const paddingBottom = 50;

  // Calculate bar widths based on whether we have comparison
  const barsPerGroup = showComparison ? barMetrics.length * 2 : barMetrics.length;
  const singleBarWidth = Math.min(12, 80 / Math.max(barsPerGroup, 1));
  const barGroupWidth = Math.max(barsPerGroup * (singleBarWidth + 2) + 20, 60);
  const chartWidth = Math.max(data.length * (barGroupWidth + 10) + paddingLeft + paddingRight, 600);
  const chartHeight = height - paddingTop - paddingBottom;

  const xStep = (chartWidth - paddingLeft - paddingRight) / data.length;
  const getX = (i: number): number => paddingLeft + i * xStep + xStep / 2;
  const getY = (val: number): number => paddingTop + chartHeight - (Math.abs(val) / maxValue) * chartHeight;

  // Generate line path
  const generateLinePath = (metricKey: string, dataSet: ClientChartDataPoint[]): string => {
    const points = dataSet.map((d, i) => {
      const value = d[metricKey as keyof ClientChartDataPoint] as number;
      return `${getX(i)},${getY(value)}`;
    });
    return `M ${points.join(' L ')}`;
  };

  return (
    <div style={{
      background: '#141414',
      borderRadius: '12px',
      padding: '1rem',
      overflowX: 'auto',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <svg width={chartWidth} height={height} style={{ display: 'block' }}>
        {/* Grid lines - dark theme */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <g key={i}>
            <line
              x1={paddingLeft}
              y1={paddingTop + chartHeight * (1 - pct)}
              x2={chartWidth - paddingRight}
              y2={paddingTop + chartHeight * (1 - pct)}
              stroke="#2a2a2a"
              strokeDasharray={pct === 0 ? '' : '4'}
            />
            <text
              x={paddingLeft - 8}
              y={paddingTop + chartHeight * (1 - pct) + 4}
              textAnchor="end"
              fontSize="11"
              fill="#a0a0a0"
              fontFamily='Inter, system-ui, sans-serif'
            >
              {formatCompactCurrency(maxValue * pct)}
            </text>
          </g>
        ))}

        {/* Bars for each period */}
        {data.map((d, periodIdx) => {
          const x = getX(periodIdx);
          const barBottom = paddingTop + chartHeight;
          const compD = comparisonData?.[periodIdx];

          // Calculate total width needed for all bars
          const totalBars = showComparison ? barMetrics.length * 2 : barMetrics.length;
          const totalBarWidth = totalBars * singleBarWidth + (totalBars - 1) * 2;
          const startX = x - totalBarWidth / 2;

          return (
            <g key={d.period}>
              {barMetrics.map((metric, metricIdx) => {
                const value = Math.abs(d[metric.key as keyof ClientChartDataPoint] as number);
                const barHeight = (value / maxValue) * chartHeight;

                if (showComparison && compD) {
                  // Side-by-side bars: current + comparison
                  const compValue = Math.abs(compD[metric.key as keyof ClientChartDataPoint] as number);
                  const compBarHeight = (compValue / maxValue) * chartHeight;
                  const pairIdx = metricIdx * 2;
                  const currentBarX = startX + pairIdx * (singleBarWidth + 2);
                  const compBarX = startX + (pairIdx + 1) * (singleBarWidth + 2);

                  return (
                    <g key={`${d.period}-${metric.key}`}>
                      {/* Current period bar */}
                      <rect
                        x={currentBarX}
                        y={barBottom - barHeight}
                        width={singleBarWidth}
                        height={Math.max(barHeight, 1)}
                        fill={metric.color}
                        rx="2"
                        opacity={0.9}
                      />
                      {/* Comparison period bar (lighter) */}
                      <rect
                        x={compBarX}
                        y={barBottom - compBarHeight}
                        width={singleBarWidth}
                        height={Math.max(compBarHeight, 1)}
                        fill={lightenColor(metric.color, 40)}
                        rx="2"
                        opacity={0.6}
                        strokeDasharray="2"
                        stroke={metric.color}
                        strokeWidth="0.5"
                      />
                    </g>
                  );
                } else {
                  // Single bars (no comparison)
                  const barX = startX + metricIdx * (singleBarWidth + 2);
                  return (
                    <rect
                      key={`${d.period}-${metric.key}`}
                      x={barX}
                      y={barBottom - barHeight}
                      width={singleBarWidth}
                      height={Math.max(barHeight, 1)}
                      fill={metric.color}
                      rx="2"
                      opacity={0.85}
                    />
                  );
                }
              })}
            </g>
          );
        })}

        {/* Lines for current data */}
        {lineMetrics.map(metric => (
          <g key={metric.key}>
            <path
              d={generateLinePath(metric.key, data)}
              fill="none"
              stroke={metric.color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {data.map((d, i) => {
              const value = d[metric.key as keyof ClientChartDataPoint] as number;
              return (
                <g key={`${metric.key}-${d.period}`}>
                  <circle
                    cx={getX(i)}
                    cy={getY(value)}
                    r="5"
                    fill={metric.color}
                    stroke="#141414"
                    strokeWidth="2"
                  />
                  <text
                    x={getX(i)}
                    y={getY(value) - 10}
                    textAnchor="middle"
                    fontSize="9"
                    fill={metric.color}
                    fontWeight="600"
                    fontFamily='Inter, system-ui, sans-serif'
                  >
                    {formatCompactCurrency(value)}
                  </text>
                </g>
              );
            })}
          </g>
        ))}

        {/* Lines for comparison data (dashed) */}
        {showComparison && comparisonData && lineMetrics.map(metric => (
          <g key={`comp-${metric.key}`}>
            <path
              d={generateLinePath(metric.key, comparisonData)}
              fill="none"
              stroke={lightenColor(metric.color, 30)}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="6,3"
            />
            {comparisonData.map((d, i) => {
              const value = d[metric.key as keyof ClientChartDataPoint] as number;
              return (
                <circle
                  key={`comp-${metric.key}-${d.period}`}
                  cx={getX(i)}
                  cy={getY(value)}
                  r="4"
                  fill={lightenColor(metric.color, 30)}
                  stroke="#141414"
                  strokeWidth="1"
                />
              );
            })}
          </g>
        ))}

        {/* X-axis labels - dark theme */}
        {data.map((d, i) => (
          <text
            key={`label-${d.period}`}
            x={getX(i)}
            y={height - 15}
            textAnchor="middle"
            fontSize="11"
            fill="#a0a0a0"
            fontFamily='Inter, system-ui, sans-serif'
          >
            {d.label}
          </text>
        ))}

        {/* Legend - dark theme */}
        {activeMetrics.map((metric, i) => {
          const legendY = paddingTop + i * 18;
          const isLine = chartType === 'line' || (chartType === 'combo' && metric.type === 'line');

          return (
            <g key={metric.key}>
              {isLine ? (
                <>
                  <line
                    x1={chartWidth - 140}
                    y1={legendY + 6}
                    x2={chartWidth - 128}
                    y2={legendY + 6}
                    stroke={metric.color}
                    strokeWidth="3"
                  />
                  <circle cx={chartWidth - 134} cy={legendY + 6} r="3" fill={metric.color} />
                </>
              ) : (
                <>
                  <rect
                    x={chartWidth - 140}
                    y={legendY}
                    width={showComparison ? 6 : 12}
                    height={12}
                    fill={metric.color}
                    rx="2"
                  />
                  {showComparison && (
                    <rect
                      x={chartWidth - 133}
                      y={legendY}
                      width={6}
                      height={12}
                      fill={lightenColor(metric.color, 40)}
                      rx="2"
                      opacity={0.6}
                    />
                  )}
                </>
              )}
              <text
                x={chartWidth - 122}
                y={legendY + 10}
                fontSize="10"
                fill="#e0e0e0"
                fontFamily='Inter, system-ui, sans-serif'
              >
                {metric.label}
              </text>
            </g>
          );
        })}

        {/* Comparison legend indicator */}
        {showComparison && (
          <g>
            <rect
              x={chartWidth - 140}
              y={paddingTop + activeMetrics.length * 18 + 8}
              width={18}
              height={1}
              fill="#4a4a4a"
            />
            <text
              x={chartWidth - 118}
              y={paddingTop + activeMetrics.length * 18 + 12}
              fontSize="9"
              fill="#a0a0a0"
              fontStyle="italic"
              fontFamily='Inter, system-ui, sans-serif'
            >
              Comparação
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

export default ClientMatrixChart;
